import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useAuth, startGoogleLogin } from "../context/AuthContext";

const GoogleGlyph = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.9 1.5l2.6-2.5C16.9 2.9 14.7 2 12 2 6.9 2 2.8 6.1 2.8 11.2S6.9 20.4 12 20.4c5.9 0 9.8-4.1 9.8-9.9 0-.7-.1-1.2-.2-1.7H12z" />
  </svg>
);

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.error || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.user_id) navigate("/account", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/account", { replace: true });
    else setError(res.error || "Login failed.");
  };

  return (
    <div className="min-h-screen pt-16 flex items-center justify-center px-5 bg-[#0F0F10]">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md border border-white/10 bg-[#1F1F1F] p-8 sm:p-10"
      >
        <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#4ADE80] mb-4">Account</p>
        <h1 className="font-display font-black text-5xl tracking-tighter uppercase leading-none">Sign In</h1>
        <p className="mt-4 text-[#A8A8A8] text-sm">Access your orders and reorder research compounds.</p>

        {error && (
          <p data-testid="login-error" className="mt-6 border border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444] font-mono-accent text-xs px-4 py-3">
            {error}
          </p>
        )}

        <form onSubmit={submit} data-testid="login-form" className="mt-8 space-y-4">
          <input data-testid="login-email" required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-[#0F0F10] border border-white/10 px-4 py-4 font-mono-accent text-sm outline-none focus:border-[#4ADE80] transition-colors placeholder:text-[#7a7a7a]" />
          <input data-testid="login-password" required type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-[#0F0F10] border border-white/10 px-4 py-4 font-mono-accent text-sm outline-none focus:border-[#4ADE80] transition-colors placeholder:text-[#7a7a7a]" />
          <button data-testid="login-submit" type="submit" disabled={loading} className="btn-press w-full bg-[#4ADE80] text-[#0F0F10] font-medium py-4 hover:bg-[#6FE998] hover:shadow-lg hover:shadow-[#4ADE80]/20 transition-[background-color,box-shadow] flex items-center justify-center gap-2 disabled:opacity-50">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <div className="mt-4 text-right">
          <Link to="/forgot-password" data-testid="forgot-password-link" className="font-mono-accent text-xs text-[#7a7a7a] hover:text-[#4ADE80] link-underline">Forgot password?</Link>
        </div>

        <div className="my-6 flex items-center gap-4">
          <span className="h-px flex-1 bg-[#1F1F1F]" />
          <span className="font-mono-accent text-[10px] uppercase tracking-widest text-[#7a7a7a]">or</span>
          <span className="h-px flex-1 bg-[#1F1F1F]" />
        </div>

        <button data-testid="google-login-btn" onClick={startGoogleLogin} className="w-full btn-press border border-white/10 rounded-full py-4 text-[13px] font-medium flex items-center justify-center gap-3 hover:bg-[#171717] hover:border-white/25 transition-[background-color,border-color]">
          <GoogleGlyph /> Continue with Google
        </button>

        <p className="mt-8 font-mono-accent text-xs text-[#7a7a7a] text-center">
          No account?{" "}
          <Link to="/register" data-testid="go-to-register" className="text-[#4ADE80] link-underline">Create one</Link>
        </p>
      </motion.div>
    </div>
  );
}
